import {checkTileForAnchorAndConnections, Direction, findWordInDirection, getKeyGenerator} from "./utils";
import {Letter, TileCoordinateType, TileData} from "./Tile";
import {addNTimes, GRID_LENGTH, GridSpotType, INITIAL_TYPE_GRID, TRAY_LENGTH} from "./constants";
import {DICTIONARY} from "./dictionary";

type BoardView = Array<Letter>;
const ALPHABET = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

interface Move {
    letter: Letter,
    i: number,
    j: number,
}

interface CompleteMove {
    moves: Array<Move>;
    points: number;
    tilePoints: number;
}

interface MoveScoring {
    mainScoring: number;
    crossScoring: number;
    multiplier: number;
}

export class AI {
    boardView: BoardView;
    columnCrossTiles: Array<Map<String, number>>;
    rowCrossTiles: Array<Map<String, number>>;
    aiDictionary: Set<String>;
    subStrings: Set<String>;
    aiRandomness: number;

    constructor(proportionDictionary: number, aiRandomness: number){
        const difficulty = 1.0 - proportionDictionary;
        this.aiDictionary = new Set<String>();
        this.subStrings = new Set<String>();
        for(let i=0; i<DICTIONARY.word.length; i++){
            if(DICTIONARY.score[i] >= difficulty){
                const word: String = DICTIONARY.word[i];
                this.aiDictionary.add(word);

                for(let j=0; j<word.length; j++){
                    for(let k=j+1; k<word.length; k++){
                        this.subStrings.add(word.substring(j, k));
                    }
                }
            }
        }

        this.aiRandomness = aiRandomness;
        this.boardView = [];
        addNTimes(this.boardView, null, GRID_LENGTH*GRID_LENGTH);

        this.columnCrossTiles = [];
        addNTimes(this.columnCrossTiles, null, GRID_LENGTH*GRID_LENGTH);
        this.rowCrossTiles = this.columnCrossTiles.slice();

        this.updateView = this.updateView.bind(this);
        this.evaluateLetterAtSpot = this.evaluateLetterAtSpot.bind(this);
        this.evaluateTextAtSpot = this.evaluateTextAtSpot.bind(this);
        this.evaluateSpotHeadingLeft = this.evaluateSpotHeadingLeft.bind(this);
        this.evaluateSpotHeadingRight = this.evaluateSpotHeadingRight.bind(this);
        this.findAllMoves = this.findAllMoves.bind(this);
        this.findBestMove = this.findBestMove.bind(this);
        this.getLettersThatMakeWords = this.getLettersThatMakeWords.bind(this);
    }

    updateView(grid: Array<TileData>){

        const updatedRows = new Set<number>();
        const updatedColumns = new Set<number>();


        for(let i=0; i<GRID_LENGTH; i++){
            for(let j=0; j<GRID_LENGTH; j++){
                const key = GRID_LENGTH*i + j;

                let newLetter: Letter = null;
                if(grid[key] != null){
                    newLetter = grid[key].letter;
                }

                if(this.boardView[key] != newLetter){
                    this.boardView[key] = newLetter;
                    updatedRows.add(i);
                    updatedColumns.add(j);
                }

            }
        }

        const checkForValidMoves = (i: number, j:number) => {
            const key = i * GRID_LENGTH + j;
            let validMovesRows: Map<String, number> = null;
            let validMovesColumns: Map<String, number> = null;
            if (grid[key] == null) {
                validMovesRows = this.getLettersThatMakeWords(grid, Direction.ROW, i, j);
                validMovesColumns = this.getLettersThatMakeWords(grid, Direction.COLUMN, i, j);
            }

            //this.crossTiles[key] = intersectMaps(validMovesRows, validMovesColumns);
            this.columnCrossTiles[key] = validMovesColumns;
            this.rowCrossTiles[key] = validMovesRows;
        };

        updatedRows.forEach((i) => {
            for(let j=0; j<GRID_LENGTH; j++){
                checkForValidMoves(i, j);
            }
        });
        updatedColumns.forEach((j) => {
            for(let i=0; i<GRID_LENGTH; i++){
                checkForValidMoves(i, j);
            }
        });
    }

    evaluateLetterAtSpot(
        copiedLine: Array<Letter>,
        crossLettersLine: Array<Map<String, number>>,
        i: number,
        availableLetters: Array<Letter>,
        currentMoveOrder: Array<Move>,
        moveSet: Array<CompleteMove>,
        retrieveCoords: (x) => {i: number; j:number},
        keyFunction: (x) => number,
        k: string,
        minLength: number,
        currentPoints: MoveScoring,

    ){
        const letter: Letter = availableLetters[k];

        if(letter.is_blank){
            for(let j in ALPHABET){
                const letterCopy: Letter = {
                    str: ALPHABET[j],
                    is_blank: true,
                    points: letter.points
                };
                this.evaluateTextAtSpot(
                    copiedLine,
                    crossLettersLine,
                    i,
                    availableLetters,
                    currentMoveOrder,
                    moveSet,
                    retrieveCoords,
                    keyFunction,
                    k,
                    minLength,
                    currentPoints,
                    letterCopy
                );
            }
        } else {
            this.evaluateTextAtSpot(
                copiedLine,
                crossLettersLine,
                i,
                availableLetters,
                currentMoveOrder,
                moveSet,
                retrieveCoords,
                keyFunction,
                k,
                minLength,
                currentPoints,
                letter
            );
        }

    }

    // Checks for a word at the given spot for a given letter. The letter must be pre-handled in case it's a blank so that it has text.
    evaluateTextAtSpot(
        copiedLine: Array<Letter>,
        crossLettersLine: Array<Map<String, number>>,
        i: number,
        availableLetters: Array<Letter>,
        currentMoveOrder: Array<Move>,
        moveSet: Array<CompleteMove>,
        retrieveCoords: (x) => {i: number; j:number},
        keyFunction: (x) => number,
        k: string,
        minLength: number,
        currentPoints: MoveScoring,
        letter: Letter,
    ){
        copiedLine = copiedLine.slice();
        const crossLetters: Map<String, number> = crossLettersLine[i];

        // don't bother checking a letter if it's not a crossletter
        if(crossLetters != null && !crossLetters.has(letter.str)){
            return;
        }

        copiedLine[i] = letter;

        // Check if this forms a valid substring; if not then we can just return now
        const word: string = findWordOnLine(copiedLine, i);
        if(!this.subStrings.has(word)){
            return;
        }

        const expandedMoveOrder = currentMoveOrder.slice();
        expandedMoveOrder.push({
            letter: letter,
            ...retrieveCoords(i)
        });

        // Need to make a copy
        currentPoints = {
            crossScoring: currentPoints.crossScoring,
            mainScoring: currentPoints.mainScoring,
            multiplier: currentPoints.multiplier,
        };

        const tileType = INITIAL_TYPE_GRID[keyFunction(i)];

        // Take care of scoring cross letters
        let crossWordScore = 0;
        if(crossLetters != null){
            crossWordScore = crossLetters.get(letter.str);
            let crossLetterScore = letter.points;

            if(tileType == GridSpotType.DOUBLE_LETTER){
                crossLetterScore *= 2;
            } else if(tileType == GridSpotType.TRIPLE_LETTER){
                crossLetterScore *= 3;
            }
            crossWordScore += crossLetterScore;

            if(tileType == GridSpotType.DOUBLE_WORD || tileType == GridSpotType.START){
                crossWordScore *= 2;
            } else if(tileType == GridSpotType.TRIPLE_WORD){
                crossWordScore *= 3;
            }
        }
        currentPoints.crossScoring += crossWordScore;

        // Take care of any word multipliers on the main axis
        if(tileType == GridSpotType.DOUBLE_WORD || tileType == GridSpotType.START){
            currentPoints.multiplier *= 2;
        } else if(tileType == GridSpotType.TRIPLE_WORD){
            currentPoints.multiplier *= 3;
        }

        if(tileType == GridSpotType.TRIPLE_LETTER){
            currentPoints.mainScoring += letter.points*3;
        } else if(tileType == GridSpotType.DOUBLE_LETTER){
            currentPoints.mainScoring += letter.points*2;
        } else{
            currentPoints.mainScoring += letter.points;
        }

        // check if this is a valid word
        if(word.length >= minLength && this.aiDictionary.has(word)){
            let tilePointsSum: number = 0;
            expandedMoveOrder.forEach((move) => {
                tilePointsSum += move.letter.points;
            })

            moveSet.push({
                moves: expandedMoveOrder.slice(),
                points: currentPoints.crossScoring + currentPoints.mainScoring*currentPoints.multiplier,
                tilePoints: tilePointsSum,
            });
        }

        const adjustedLetters = [];
        for(let l in availableLetters){
            if(l != k){
                adjustedLetters.push(availableLetters[l]);
            }
        }

        this.evaluateSpotHeadingRight(
            copiedLine,
            crossLettersLine,
            i+1,
            adjustedLetters,
            expandedMoveOrder,
            moveSet,
            retrieveCoords,
            keyFunction,
            minLength,
            currentPoints,
        );
    }

    evaluateSpotHeadingRight(
        line: Array<Letter>,
        crossLettersLine: Array<Map<String, number>>,
        i: number,
        availableLetters: Array<Letter>,
        currentMoveOrder: Array<Move>,
        moveSet: Array<CompleteMove>,
        retrieveCoords: (x) => {i: number; j:number},
        keyFunction: (x) => number,
        minLength: number,
        currentPoints: MoveScoring,

    ){

        if(i < 0 || i >= GRID_LENGTH){
            return;
        }


        if(line[i] != null){
            currentPoints.mainScoring += line[i].points;
            this.evaluateSpotHeadingRight(
                line,
                crossLettersLine,
                i+1,
                availableLetters,
                currentMoveOrder,
                moveSet,
                retrieveCoords,
                keyFunction,
                minLength,
                currentPoints,
            );
        } else{
            for(let k in availableLetters){
                this.evaluateLetterAtSpot(
                    line,
                    crossLettersLine,
                    i,
                    availableLetters,
                    currentMoveOrder,
                    moveSet,
                    retrieveCoords,
                    keyFunction,
                    k,
                    minLength,
                    currentPoints,
                );
            }

        }

    }

    evaluateSpotHeadingLeft(
        line: Array<Letter>,
        crossLettersLine: Array<Map<String, number>>,
        i: number,
        availableLetters: Array<Letter>,
        currentMoveOrder: Array<Move>,
        moveSet: Array<CompleteMove>,
        retrieveCoords: (x) => {i: number; j:number},
        keyFunction: (x) => number,
        minLength: number,
        currentPoints: MoveScoring,
    ){

        if(i < 0 || i >= GRID_LENGTH){
            return;
        }

        if(line[i] != null){
            if(!(i-1 >= 0 && line[i-1] != null && minLength==1)){
                // Basically if our starting anchor is right at the end of a word, don't bother trying to add to the beginning of the word
                // Let the anchor point at the beginning there do the work
                this.evaluateSpotHeadingLeft(
                    line,
                    crossLettersLine,
                    i-1,
                    availableLetters,
                    currentMoveOrder,
                    moveSet,
                    retrieveCoords,
                    keyFunction,
                    minLength,
                    currentPoints,
                );
            }

        } else{

            for(let k in availableLetters){
                this.evaluateLetterAtSpot(
                    line,
                    crossLettersLine,
                    i,
                    availableLetters,
                    currentMoveOrder,
                    moveSet,
                    retrieveCoords,
                    keyFunction,
                    k,
                    minLength,
                    currentPoints,
                );
            }

            this.evaluateSpotHeadingLeft(
                line,
                crossLettersLine,
                i-1,
                availableLetters,
                currentMoveOrder,
                moveSet,
                retrieveCoords,
                keyFunction,
                minLength+1, // we have to ensure that our word still reaches our anchor point
                currentPoints,
            );

        }

    }

    findAllMoves(grid: Array<TileData>, tray: Array<TileData>):Array<CompleteMove>{
        this.updateView(grid);

        const findMoves = (direction: Direction, tray: Array<TileData>, moveSet: Array<CompleteMove>) => {
            let crossTiles: Array<Map<String, number>>;
            // If you're building a word in one direction, you need to form valid words in the cross-direction
            if(direction == Direction.ROW){
                crossTiles = this.columnCrossTiles;
            } else{
                crossTiles = this.rowCrossTiles;
            }

            const trayLetters: Array<Letter> = [];
            for(let i in tray){
                if(tray[i] != null){
                    trayLetters.push(tray[i].letter);
                }
            }

            for(let k=0; k<GRID_LENGTH; k++){
                const keyFunction = getKeyGenerator(direction, k);

                const line: Array<Letter> = [];
                const lineCrossLetters: Array<Map<String, number>> = [];
                for(let p=0; p<GRID_LENGTH; p++){
                    line.push(this.boardView[keyFunction(p)]);
                    lineCrossLetters.push(crossTiles[keyFunction(p)]);
                }

                for(let l=0; l<GRID_LENGTH; l++){
                    const key = keyFunction(l);
                    const isAnchored = checkTileForAnchorAndConnections(grid, key)[1];

                    if(isAnchored){
                        let retrieveCoordsFunction: (x) => {i: number, j:number} = null;
                        if(direction == Direction.COLUMN){
                            retrieveCoordsFunction = (i) => {
                                return {
                                    i: i,
                                    j: k
                                };
                            }
                        } else{
                            retrieveCoordsFunction = (j) => {
                                return {
                                    i: k,
                                    j: j
                                };
                            }
                        }

                        // Look for moves
                        this.evaluateSpotHeadingLeft(
                            line,
                            lineCrossLetters,
                            l,
                            trayLetters,
                            [],
                            moveSet,
                            retrieveCoordsFunction,
                            keyFunction,
                            1,
                            {
                                mainScoring: 0,
                                crossScoring: 0,
                                multiplier: 1,
                            }
                        );

                    }
                }



            }

        };

        // repeat for rows and columns
        const moveSet: Array<CompleteMove> = [];
        findMoves(Direction.ROW, tray, moveSet);
        findMoves(Direction.COLUMN, tray, moveSet);

        return moveSet;


    }

    findBestMove(grid: Array<TileData>, tray: Array<TileData>):CompleteMove{
        const moveSet = this.findAllMoves(grid, tray);

        let bestMove: CompleteMove = null;
        let bestMoveScore: number = null;

        moveSet.forEach((move) => {
            let moveScore = move.points;
            if(this.aiRandomness > 0){
                moveScore = (1 - this.aiRandomness)*move.points + this.aiRandomness*Math.random();
            }

            if(bestMove == null || moveScore > bestMoveScore){
                bestMove = move;
                bestMoveScore = moveScore;
            }
        });

        return bestMove;

    }


    /*
        Idea here is that when we play a word we're on either rows or columns, so letters placed must form valid
        words in the cross-direction. For the given cross-direction, we examine all possible letters that can form
        valid words

     */
    getLettersThatMakeWords(tileData: Array<TileData>, direction: Direction, i: number, j: number){

        const key = GRID_LENGTH*i + j;
        const isAnchored = checkTileForAnchorAndConnections(tileData, key, [direction])[1];
        if(!isAnchored){
            return null;
        }

        const copyTileData = tileData.slice();

        const wordMap: Map<String, number> = new Map();
        for(let k=0; k<ALPHABET.length; k++){
            const letter = ALPHABET[k];
            copyTileData[key] = {
                letter: {
                    is_blank: false,
                    str: letter,
                    points: 0, // we account for the point scoring of the letter itself later
                },
                coordinates: null,
                isEphemeral: false, // will mean that tile bonuses aren't taken into account
                recentlyPlayed: false,
            };

            const possibleWord = findWordInDirection(copyTileData, direction, i, j);
            if(possibleWord != null){
                const possibleWordString = possibleWord.getWordAsString();
                if(this.aiDictionary.has(possibleWordString)){
                    wordMap.set(letter, possibleWord.score(copyTileData));
                }
            }
        }

        return wordMap;

    }
}


function findWordOnLine(line: Array<Letter>, anchor: number, startWord=anchor, endWord=anchor): string{

    while(startWord >= 0){
        if(startWord-1 < 0){
            break;
        }
        if(line[startWord-1] == null){
            break;
        }
        startWord = startWord - 1;
    }

    while(endWord < GRID_LENGTH){
        if(endWord+1 >= GRID_LENGTH){
            break;
        }
        if(line[endWord+1] == null){
            break;
        }
        endWord = endWord + 1;
    }

    let word = "";
    for(let i=startWord; i<=endWord; i++){
        word = word + line[i].str;
    }

    return word;


}

export function performMove(
    grid: Array<TileData>,
    //setGrid: (x: Array<TileData>) => void,
    tray: Array<TileData>,
    //setTray: (x: Array<TileData>) => void,
    completeMove: CompleteMove){

    completeMove.moves.forEach((move) => {
       const letterString = move.letter.str;
       let tile: TileData = null;

       for(let i=0; i<TRAY_LENGTH; i++){
           const currentTile = tray[i];

           if(currentTile != null){
               if(currentTile.letter.str == letterString){
                   tile = currentTile;
                   tray[i] = null;
                   break;
               } else if(currentTile.letter.is_blank && move.letter.is_blank){
                   tile = currentTile;
                   tile.letter.str = letterString;
                   tray[i] = null;
                   break;
               }
           }
       }

       if(tile == null){
           throw new Error(`There's no tile for letter ${letterString}`);
       }

       const key = move.i*GRID_LENGTH + move.j;
       tile.isEphemeral = true;
       tile.coordinates.location = TileCoordinateType.GRID;
       tile.coordinates.index = key;
       tile.recentlyPlayed = true;

       grid[key] = tile;
    });

    //setGrid(grid.slice());
    //setTray(tray.slice());

}


